import type { ReactNode } from 'react';

import { stringify } from './option';

/**
 * This constant defines the minimum length of a substring for it to evaluate as
 * a match when compared with a string. This is to prevent false matches on
 * single characters
 */
export const MINIMUM_MATCH_LENGTH = 2;

/**
 * Create a regular expression that will match any tokenized word from a string
 * of multiple words. This is useful for matching multiple words from a larger
 * sentence.
 */
export function getTokenizedWordRegex(string: string): RegExp {
  const captureGroup = string
    .trim()
    .split(/\s+/)
    .filter(word => word.length > MINIMUM_MATCH_LENGTH)
    .map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|');

  return new RegExp(`(${captureGroup})`, 'gi');
}

/**
 * Determine whether or not a string "contains" a substring according to our
 * requirements. The rules are as follows.
 *
 * - Substring can contain multiple words separated by any number of spaces
 * - Each word must exceed the minimum length to be considered
 * - The string must contain at least one of the valid words
 */
export function matchSubstring(string: string, substring: string): boolean {
  const safeString = string.toLowerCase();
  const safeSubstring = substring.toLowerCase();

  if (safeString.startsWith(safeSubstring)) {
    return true;
  }

  if (safeSubstring.length > MINIMUM_MATCH_LENGTH) {
    return safeSubstring
      .split(/\s+/)
      .every(substringWord =>
        safeString
          .split(/\s+/)
          .some(stringWord => stringWord.startsWith(substringWord))
      );
  }

  return false;
}

/**
 * Create a closure over an input string and filter string and return a function
 * that compares a stringified option to the input string.
 */
export function byInputValue(inputValue: string, ignoreFilter?: string) {
  return (option: ReactNode): boolean => {
    if (inputValue.length <= MINIMUM_MATCH_LENGTH) {
      return true;
    }

    const inputString = inputValue.toLowerCase();
    const optionString = stringify(option).toLowerCase();
    const filterString = ignoreFilter?.toLowerCase();

    if (optionString === filterString) {
      return true;
    }

    return matchSubstring(optionString, inputString);
  };
}

/**
 * Create a closure over a filter string and return a function that compares a
 * stringified option to the input string.
 */
export function byIgnoreFilter(ignoreFilter?: string) {
  return (_: ReactNode, option: ReactNode): number => {
    const optionString = stringify(option).toLowerCase();
    const filterString = ignoreFilter?.toLowerCase();

    if (optionString === filterString) {
      return -1;
    }

    return 0;
  };
}
